/**
 * Shared "client org" Cascader logic.
 *
 * A single field that selects an org: orgs that have sub-orgs (Channel Partners)
 * expand into their advertisers; orgs with no sub-orgs (Direct) are picked as-is.
 * Used by both the IO Request fields and the generic Channel Partner / Advertiser
 * selector in TaskAssignmentFields. Options are sorted alphabetically.
 */

import { useEffect, useState } from 'react';
import { useOrgContext } from '../../../../../core/components/orgs/useOrgContext';

const CLIENT_FETCH_LIMIT = 1000;

// Channel-partner org levels — the exact set the old Channel Partner query filtered by. For
// PLATFORM users only these appear at the TOP level (so non-partner orgs don't crowd it);
// advertisers under them still nest in regardless of level.
const CHANNEL_PARTNER_LEVELS = ['CHANNEL_PARTNER', 'CHANNEL_PARTNER_DIRECT', 'CORE', 'ENTERPRISE'];

// Alphabetical, case-insensitive — raw org names, no reformatting.
const byLabel = (a, b) => (a.label || '').toLowerCase().localeCompare((b.label || '').toLowerCase());

// Build a Cascader tree from a flat org list using the parentOrg links.
// Roots are orgs directly under the current user's org; a root with no children
// is a Direct client (leaf), a root with children is a Channel Partner.
export const buildClientTree = (orgs, userOrgId, level) => {
  // Child matcher = parentOrg_some (an org is a child of X when X is anywhere in its parentOrg
  // list) — the exact matcher the original Channel Partner / Advertiser queries used.
  const isChildOf = (org, parentId) => (org.parentOrg || []).some((p) => p.id === parentId);
  // Top level = your channel partners only (for PLATFORM, the same level-scoped set the old CP
  // dropdown showed) so the top stays short; their advertisers (any level) nest underneath.
  const isTopLevel = (o) =>
    isChildOf(o, userOrgId) && (level !== 'PLATFORM' || CHANNEL_PARTNER_LEVELS.includes(o.level));
  const roots = (orgs || []).filter(isTopLevel);
  return roots
    .map((root) => {
      const children = (orgs || []).filter((o) => isChildOf(o, root.id));
      return {
        value: root.id,
        label: root.name,
        isLeaf: children.length === 0,
        children: children.length
          ? children.map((c) => ({ value: c.id, label: c.name, isLeaf: true })).sort(byLabel)
          : undefined,
      };
    })
    .sort(byLabel);
};

// Fetch the user's client-org subtree (two levels) and build a sorted Cascader tree.
// Capped at CLIENT_FETCH_LIMIT — very large tenants may truncate; revisit with a
// reverse childOrgs relation + lazy loadData if that becomes a problem.
export const useClientOrgTree = (userOrgId, level) => {
  const { queryOrgs } = useOrgContext();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!userOrgId) return;
      setLoading(true);
      try {
        // Top level = your channel partners (the old CP query level-filtered for PLATFORM, so
        // non-partner orgs don't crowd the top). Second branch = their advertisers, which nest
        // under each partner via buildClientTree's parentOrg_some matcher regardless of level —
        // so an advertiser like cannabisNow still appears under its partner.
        const topLevel =
          level === 'PLATFORM'
            ? { level_in: CHANNEL_PARTNER_LEVELS, parentOrg_some: { id: userOrgId } }
            : { parentOrg_some: { id: userOrgId } };
        const data = await queryOrgs({
          first: CLIENT_FETCH_LIMIT,
          where: {
            AND: [
              { OR: [topLevel, { parentOrg_some: { parentOrg_some: { id: userOrgId } } }] },
              // Exclude archived orgs, like the Orgs module's tree query
              { OR: [{ status: null }, { status_not: 'ARCHIVED' }] },
            ],
          },
        });
        if (active) setOptions(buildClientTree(data && data.orgs ? data.orgs : [], userOrgId, level));
      } catch (e) {
        if (active) setOptions([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [queryOrgs, userOrgId, level]);

  return { options, loading };
};

// Map a Cascader path to channelPartnerId / advertiserId on the form.
// [partner, advertiser] => Channel Partner deal; [org] => Direct (org is the advertiser).
export const applyClientPath = (form, path = []) => {
  if (path.length >= 2) {
    form.setFieldsValue({ channelPartnerId: path[0], advertiserId: path[path.length - 1] });
  } else {
    form.setFieldsValue({ channelPartnerId: null, advertiserId: path[0] || null });
  }
};

// Shared Cascader search filter (matches any node label along the path)
export const cascaderSearchFilter = {
  filter: (input, path) =>
    path.some((opt) => (opt.label || '').toLowerCase().includes(input.toLowerCase())),
};
